import { useState } from 'react';
import './styles.scss';
import Modal from 'react-modal';
import { PremiacaoIsolada, Combo } from '../../../types/experienceCatalog';
import { getExperienceTypeTitle } from 'utils/experience-catalog';
import { Countdown } from 'components/countdown';
import catalogStar from 'assets/img/experienceCatalog/catalog-star.svg';
import { IoMdInformationCircleOutline } from 'react-icons/io';

Modal.setAppElement('#root');

interface ExperienceCardProps {
  item: PremiacaoIsolada | Combo;
  validity: Date;
  type: 'unico' | 'combo';
  purchaseExperience: (item: PremiacaoIsolada | Combo) => void;
  ruleUrl: string;
}

export default function ExperienceSelectCard({ item, validity, type, purchaseExperience, ruleUrl }: ExperienceCardProps) {
  const [flipped, setFlipped] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [rulePage, setRulePage] = useState(0);
  const [loading, setLoading] = useState(false);

  const getTitle = () => item.title;
  const getQuantity = () => {
    if (type === 'combo' && 'totalDias' in item) {
      return item.totalDias;
    }
    if (type === 'unico' && 'total' in item && item.type !== 'pontos_Livelo') {
      return item.total;
    }
    if (type === 'unico' && 'valor' in item) {
      return item.valor.toLocaleString('pt-BR');
    }
    return 1;
  };
  const getDescription = () => {
    if (type === 'combo' && 'description' in item) {
      return item.regrasDeUso.subtitle;
    }
    return getTitle();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!flipped) {
        setFlipped(true);
      } else {
        setShowModal(true);
      }
    }
    if (event.key === 'Escape' && flipped) {
      setFlipped(false);
    }
  };

  return (
    <article
      className="relative w-[300px] h-[450px] focus-within:ring-2 focus-within:ring-white focus-within:ring-offset-2 transition-all"
      style={{ perspective: '1000px' }}
      aria-labelledby={`experience-${item.itemIdPurchase}-title`}
      aria-describedby={`experience-${item.itemIdPurchase}-desc`}
    >
      <div
        className={`relative w-full h-full duration-500 ${flipped ? '' : ''}`}
        style={{
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
        role="region"
        aria-live="polite"
        aria-atomic="true"
      >
        <div
          className={`absolute w-full h-full bg-[#002647] border-2 ${type === 'combo' ? 'border-[#FFB7AB]' : 'border-[#3962FF]'} rounded-xl flex flex-col justify-between overflow-hidden`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="p-4 relative">
            <span
              className={`absolute right-4 top-4 ${type === 'combo' ? 'bg-[#FFB7AB]' : 'bg-[#3962FF]'} text-white text-xs px-4 py-1 rounded-full font-medium`}
              role="status"
              aria-label={`Tipo de experiência: ${type === 'combo' ? 'Combinação' : 'Única'}`}
            >
              {type}
            </span>
            <h2
              id={`experience-${item.itemIdPurchase}-title`}
              className="text-white font-extrabold text-3xl leading-tight mt-12 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
              tabIndex={0}
            >
              {type === 'unico' && getQuantity()}
              {type === 'unico' && <br />}
              {type === 'unico'
                ? getExperienceTypeTitle((item as PremiacaoIsolada).type, (item as PremiacaoIsolada).valor)
                : item.title}
            </h2>
            <div className="text-yellow-400 text-2xl mt-4 flex items-center">
              <img src={catalogStar} alt="Ícone de estrela do catálogo" className="w-6 h-6" />
              <p className="text-yellow text-sm ml-2">{item.regrasDeUso?.subtitle && item.regrasDeUso.subtitle}</p>
            </div>
            <p
              id={`experience-${item.itemIdPurchase}-desc`}
              className="text-white mt-4 text-sm font-extralight leading-relaxed focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
              tabIndex={0}
            >
              {type === 'combo' ? (item as Combo).description : (item as PremiacaoIsolada).description}
            </p>
            {item.amount > 1 && (
              <p className="flex items-center gap-2 text-blue-light text-sm mt-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all" tabIndex={0}>
                <IoMdInformationCircleOutline aria-hidden="true" />
                <span>Quantidade de vagas disponíveis: {item.amount}</span>
              </p>
            )}
          </div>
          <button
            tabIndex={-1}
            onClick={() => setFlipped(true)}
            onKeyDown={handleKeyDown}
            className={`${type === 'combo' ? 'bg-[#FFB7AB] hover:bg-[#FF9B8A]' : 'bg-[#3962FF] hover:bg-[#2948CC]'} text-white py-4 font-bold w-full border-t border-dashed border-prussian-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all`}
          >
            QUERO VIVER ESSA EXPERIÊNCIA
          </button>
        </div>

        {/* Verso do Card */}
        <div
          className="absolute w-full h-full bg-white border-2 border-[#3962FF] rounded-xl flex flex-col justify-between overflow-hidden"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
          aria-label="Verso do card com regras de uso"
        >
          <div className="p-4 relative">
            <button
              onClick={() => setFlipped(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setFlipped(false);
                }
              }}
              className="absolute right-4 top-4 text-[#002647] font-bold hover:text-[#FF0000] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-2 transition-all w-8 h-8 flex items-center justify-center rounded-full"
              aria-label="Voltar para frente do card"
            >
              ✕
            </button>
            <h3 className="bg-yellow text-black font-bold inline-block px-2 py-1 mb-2 rounded">Regras de uso:</h3>
            <h4 className="text-[#002647] font-bold mb-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-2 transition-all" tabIndex={0}>
              {getTitle()}
            </h4>
            {/* <p className="text-[#002647] text-sm mb-4">{item.regrasDeUso?.subtitle}</p> */}
            {/* Carrossel de regras de uso */}
            {item.regrasDeUso?.rules2
              ? (() => {
                  const rules = [item.regrasDeUso.rules1, item.regrasDeUso.rules2];
                  return (
                    <div>
                      <div
                        className="text-sm text-[#002647] list-disc pl-5 space-y-1 custom-list-disc-yellow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-2 transition-all"
                        dangerouslySetInnerHTML={{ __html: rules[rulePage] }}
                        tabIndex={0}
                        role="region"
                        aria-live="polite"
                        aria-atomic="true"
                        aria-label={`Regras de uso, página ${rulePage + 1} de 2`}
                      />
                      <nav className="flex flex-col items-center justify-center mb-2" aria-label="Navegação das páginas de regras">
                        <div className="flex items-center gap-2 mb-1" role="tablist">
                          {[0, 1].map(idx => (
                            <button
                              key={idx}
                              type="button"
                              role="tab"
                              aria-selected={rulePage === idx}
                              aria-label={`Página ${idx + 1} das regras`}
                              onClick={() => setRulePage(idx)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setRulePage(idx);
                                }
                              }}
                              className={`carousel-dot focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-1 transition-all${rulePage === idx ? ' active' : ''}`}
                            />
                          ))}
                        </div>
                        <span
                          className="text-[#102535] text-sm font-normal leading-none"
                          role="status"
                          aria-live="polite"
                        >
                          {rulePage + 1} de 2
                        </span>
                      </nav>
                    </div>
                  );
                })()
              : item.regrasDeUso?.rules1 && (
                  <div
                    className="text-sm text-[#002647] list-disc pl-5 space-y-1 custom-list-disc-yellow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-2 transition-all"
                    dangerouslySetInnerHTML={{ __html: item.regrasDeUso.rules1 }}
                    tabIndex={0}
                    role="region"
                    aria-label="Regras de uso da experiência"
                  />
                )}
          </div>
          <button
            onClick={() => setShowModal(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowModal(true);
              }
            }}
            className="bg-yellow hover:bg-yellow-400 text-[#002647] py-4 font-bold w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-2 transition-all"
            aria-label={`Confirmar seleção da experiência: ${getTitle()}`}
          >
            CONFIRMO QUE QUERO VIVER ESSA EXPERIÊNCIA
          </button>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          setIsChecked(false);
        }}
        contentLabel="Modal de confirmação da experiência selecionada"
        className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 relative outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        ariaHideApp={false}
        shouldFocusAfterRender={true}
        shouldReturnFocusAfterClose={true}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <button
          onClick={() => {
            setShowModal(false);
            setIsChecked(false);
          }}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 text-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all w-8 h-8 flex items-center justify-center rounded-full"
          aria-label="Fechar modal de confirmação"
        >
          ✕
        </button>

        <div className="text-center">
          <h2
            id="modal-title"
            className="text-xl mb-4 text-[#002647] font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all"
            tabIndex={0}
          >
            Atenção!
          </h2>
          <p
            id="modal-description"
            className="mb-6 text-prussian-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all"
            tabIndex={0}
          >
            Quando você escolher viver uma experiência do catálogo, essa será sua escolha definitiva.
            <strong> Após a confirmação, não será possível realizar trocas.</strong>
          </p>

          <div className="bg-gray-100 rounded-lg p-4 mb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all" tabIndex={0}>
            <h3 className="font-bold text-[#002647] mb-2">Sua escolha:</h3>
            <p className="text-base text-[#002647] font-bold">
              {type === 'unico' && getQuantity()} <strong>{getTitle()}</strong>
              {type === 'combo' && <span className="block text-gray-600">{getDescription()}</span>}
              {type === 'combo' && (item as Combo).description}
            </p>
          </div>
          <fieldset className="flex items-start gap-3 mb-6 text-left">
            <legend className="sr-only">Confirmação de ciência do regulamento</legend>
            <input
              type="checkbox"
              id={`regulation-checkbox-${item.itemIdPurchase}`}
              checked={isChecked}
              onChange={e => setIsChecked(e.target.checked)}
              className="mt-1 w-4 h-4 text-[#3962FF] bg-gray-100 border-gray-300 rounded focus:ring-[#3962FF] focus:ring-2 focus:ring-offset-1"
              aria-describedby={`regulation-desc-${item.itemIdPurchase}`}
            />
            <label
              htmlFor={`regulation-checkbox-${item.itemIdPurchase}`}
              className="text-sm text-gray-700 cursor-pointer hover:text-gray-900 transition-colors"
            >
              <span id={`regulation-desc-${item.itemIdPurchase}`}>
                Declaro que estou ciente do{' '}
                <a
                  href={ruleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-[#2948CC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2948CC] focus-visible:ring-offset-1 transition-colors font-medium"
                  onClick={e => e.stopPropagation()}
                  aria-label="Abrir regulamento em nova aba"
                >
                  Regulamento
                </a>{' '}
                e das diretrizes do programa
              </span>
            </label>
          </fieldset>
          <div className="bg-prussian-blue border border-yellow-400 rounded-lg p-4 mb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-2 transition-all" tabIndex={0} role="timer" aria-live="polite">
            <p className="text-sm text-yellow font-medium">
              Prazo para escolha: <Countdown deadline={validity} />
            </p>
          </div>

          <div className="flex gap-3" role="group" aria-label="Ações de confirmação">
            <button
              onClick={() => setShowModal(false)}
              className="flex-1 uppercase bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 transition-colors"
              aria-label="Voltar sem confirmar a experiência"
            >
              Voltar
            </button>
            <button
              onClick={async () => {
                setLoading(true);
                try {
                  await purchaseExperience(item);
                } finally {
                  setLoading(false);
                }
              }}
              disabled={!isChecked || loading}
              className={`flex-1 py-3 px-4 uppercase rounded-lg font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                isChecked && !loading
                  ? 'bg-[#3962FF] text-white hover:bg-[#2948CC] focus-visible:ring-[#3962FF]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed focus-visible:ring-gray-400'
              }`}
              aria-label={
                loading
                  ? 'Processando confirmação da experiência'
                  : !isChecked
                    ? 'Marque a confirmação do regulamento para continuar'
                    : `Confirmar experiência: ${getTitle()}`
              }
              aria-describedby={!isChecked ? `regulation-desc-${item.itemIdPurchase}` : undefined}
            >
              {loading ? 'Processando...' : 'quero viver essa experiência'}
            </button>
          </div>
        </div>
      </Modal>
    </article>
  );
}
